// Conexão com o servidor da mesa. Guarda o que o servidor manda, confere o seq dos eventos
// e reconecta sozinha (inclusive depois de um deploy, que fecha com o código 4000).
import { limiteDaMesa } from '../comum/limite.ts';
import type { GanhosDaPartida } from '@truco6/gamificacao';
import type { HouseRules, PlayerView, Seat } from '@truco6/regras';
import type {
  CodigoErro, EventoNumerado, MensagemCliente, MensagemServidor, Perfil, Prazo, SalaPublica, VotacaoVisivel,
} from '@truco6/mesa/protocolo';

export interface SocketLike {
  readyState: number;
  send(dados: string): void;
  close(codigo?: number, motivo?: string): void;
  onopen: ((ev: unknown) => void) | null;
  onmessage: ((ev: { data: unknown }) => void) | null;
  onclose: ((ev: { code: number }) => void) | null;
  onerror: ((ev: unknown) => void) | null;
}

export type StatusConexao = 'conectando' | 'aberta' | 'reconectando' | 'encerrada';

export interface EstadoMesa {
  status: StatusConexao;
  erro: { codigo: CodigoErro | 'rede'; mensagem: string } | null;
  aviso: { codigo: CodigoErro; mensagem: string; em: number } | null; // ação recusada, some sozinho
  usuario: string | null;
  anonimo: boolean;
  sala: SalaPublica | null;
  eu: Seat | null;
  partida: number;
  seq: number;
  visao: PlayerView | null;
  votacao: VotacaoVisivel | null;
  prazos: Prazo[];
  desvio: number; // horário do servidor menos o horário do aparelho
  ganhos: { partida: number; ganhos: GanhosDaPartida } | null; // o que a última partida rendeu (pontos do ranking e PD)
  rodagemAoVivo: { id: number; partida: number; lance: string; quanto: number; total: number } | null; // o último lance seu
  zoeira: boolean; // a sua chave "Zoeira na mesa", como a mesa leu (desligada: sem a aba Zoeira)
  faltaCelular: boolean; // a mesa recusou sentar porque a conta Google não tem celular: a folha "Falta seu celular" abre
  // Limite do Grátis: quantas partidas grátis sobram hoje, recebido quando a partida começa (ou ao sentar na vaga).
  limite: { partida: number; restantes: number; origem: 'diaria' | 'bonus'; em: number } | null;
  semPartidas: boolean; // a mesa recusou sentar (ou levantou antes de começar): as partidas grátis de hoje acabaram
}

export type OuvinteEventos = (eventos: EventoNumerado[], estado: EstadoMesa) => void;
// Recados que só animam a mesa: reação, opinião, frase e quem sentou na vaga aberta.
export type OuvinteRecado = (m: Extract<MensagemServidor, { t: 'reacao' | 'opiniao' | 'frase' | 'sentou' | 'reacao_plateia' }>) => void;

// O que a Mesa usa de uma conexão: a de quem joga (ConexaoMesa) ou a da plateia (ConexaoPlateia).
export interface FonteDaMesa {
  obter(): EstadoMesa;
  assinar(fn: () => void): () => void;
  aoEventos(fn: OuvinteEventos): () => void;
  aoRecado(fn: OuvinteRecado): () => void;
  enviar(m: MensagemCliente): boolean;
  encerrar(): void;
  agoraServidor(): number;
}

const ABERTO = 1;
const FATAIS: CodigoErro[] = ['sala_inexistente', 'sala_cheia', 'token_invalido', 'outra_conexao', 'sala_encerrada', 'conta_obrigatoria'];
// Recusas na porta de uma sala valendo Tentos ou de uma mesa de comunidade: antes de entrar, viram tela; dentro da sala, só aviso.
const NA_ENTRADA: CodigoErro[] = ['sem_tentos', 'tentos_bloqueados', 'tentos_desligados', 'so_membros'];

export interface NovaSala {
  regras?: Partial<HouseRules>;
  nome?: string;
  publica?: boolean;
  ingresso?: number | null; // Tentos por jogador; sem ingresso, a sala é grátis
  plateia?: boolean; // sala privada: plateia ligada (padrão) ou desligada
  comunidade?: string; // mesa da comunidade: só os membros veem e entram
}

export class ConexaoMesa {
  private ws: SocketLike | null = null;
  private estado: EstadoMesa = {
    status: 'conectando', erro: null, aviso: null, usuario: null, anonimo: true, sala: null, eu: null,
    partida: 0, seq: 0, visao: null, votacao: null, prazos: [], desvio: 0, ganhos: null, rodagemAoVivo: null, zoeira: true,
    faltaCelular: false, limite: null, semPartidas: false,
  };
  private readonly ouvintes = new Set<() => void>();
  private readonly ouvintesEventos = new Set<OuvinteEventos>();
  private readonly ouvintesRecado = new Set<OuvinteRecado>();
  private tentativas = 0;
  private encerrada = false;
  private pendenteCriar: { ok: (c: string) => void; falha: (e: Error) => void } | null = null;
  // O último pedido para sentar (o lugar, a vaga aberta ou a entrada na mesa do campeonato): volta depois do celular salvo.
  private sentarPedido: MensagemCliente | null = null;
  private timer: ReturnType<typeof setTimeout> | null = null;

  private readonly url: string;
  private readonly token: () => Promise<string>;
  private perfil: Perfil;
  private codigo: string | null;
  private readonly criarSocket: (url: string) => SocketLike;

  constructor(
    url: string, token: () => Promise<string>, perfil: Perfil, codigo: string | null,
    criarSocket: (url: string) => SocketLike = (u) => new WebSocket(u) as unknown as SocketLike,
  ) {
    this.url = url;
    this.token = token;
    this.perfil = perfil;
    this.codigo = codigo;
    this.criarSocket = criarSocket;
  }

  // ---- para o React (useSyncExternalStore) ----
  obter = (): EstadoMesa => this.estado;
  assinar = (fn: () => void): (() => void) => { this.ouvintes.add(fn); return () => this.ouvintes.delete(fn); };
  aoEventos(fn: OuvinteEventos): () => void { this.ouvintesEventos.add(fn); return () => this.ouvintesEventos.delete(fn); }
  aoRecado(fn: OuvinteRecado): () => void { this.ouvintesRecado.add(fn); return () => this.ouvintesRecado.delete(fn); }

  agoraServidor(): number { return Date.now() + this.estado.desvio; }

  iniciar(): this { this.conectar(); return this; }

  encerrar(): void {
    this.encerrada = true;
    if (this.timer) clearTimeout(this.timer);
    this.ws?.close(1000, 'saiu');
    this.mudar({ status: 'encerrada' });
  }

  enviar(m: MensagemCliente): boolean {
    if (m.t === 'sentar' || m.t === 'entrar') this.sentarPedido = m;
    if (this.ws?.readyState !== ABERTO) return false;
    this.ws.send(JSON.stringify(m));
    return true;
  }

  entrar(codigo: string, perfil = this.perfil): void {
    this.codigo = codigo;
    this.perfil = perfil;
    this.enviar({ t: 'entrar', codigo, perfil });
  }

  // Celular salvo na folha: tenta sentar de novo no mesmo lugar, sem a pessoa tocar outra vez.
  sentarDeNovo(): void {
    this.mudar({ faltaCelular: false });
    if (this.sentarPedido) this.enviar(this.sentarPedido);
  }

  // "Agora não": a folha fecha e a pessoa continua fora da mesa.
  deixarParaDepois(): void { this.mudar({ faltaCelular: false }); }

  // Sem regras, a sala nasce com as regras padrão. Regras da casa e ingresso só passam no servidor com Prata ou Ouro.
  criarSala(nova: NovaSala = {}): Promise<string> {
    const m: Extract<MensagemCliente, { t: 'criar' }> = { t: 'criar' };
    if (nova.regras) m.regras = nova.regras;
    if (nova.nome) m.nome = nova.nome;
    if (nova.publica) m.publica = true;
    if (nova.ingresso) m.ingresso = nova.ingresso;
    if (nova.plateia === false) m.plateia = false;
    if (nova.comunidade) m.comunidade = nova.comunidade;
    return new Promise((ok, falha) => {
      this.pendenteCriar = { ok, falha };
      if (!this.enviar(m)) { this.pendenteCriar = null; falha(new Error('Sem conexão com a mesa.')); }
    });
  }

  // A sala do jogo de campeonato: a mesma para os dois trios, criada no primeiro acesso dentro da janela.
  salaDoCampeonato(jogo: string): Promise<string> {
    return new Promise((ok, falha) => {
      this.pendenteCriar = { ok, falha };
      if (!this.enviar({ t: 'campeonato', jogo })) { this.pendenteCriar = null; falha(new Error('Sem conexão com a mesa.')); }
    });
  }

  // ---- conexão ----
  private conectar(): void {
    if (this.encerrada) return;
    const ws = this.ws = this.criarSocket(this.url);
    ws.onopen = () => {
      void this.token().then(
        (token) => { if (this.ws === ws) ws.send(JSON.stringify({ t: 'ola', token })); },
        () => this.mudar({ erro: { codigo: 'token_invalido', mensagem: 'Sua sessão venceu. Entre de novo.' } }),
      );
    };
    ws.onmessage = (ev) => {
      try { this.receber(JSON.parse(String(ev.data)) as MensagemServidor); } catch { /* mensagem quebrada */ }
    };
    ws.onclose = (ev) => {
      if (this.ws !== ws) return;
      this.ws = null;
      if (this.encerrada || ev.code === 4001 || (this.estado.erro && FATAIS.includes(this.estado.erro.codigo as CodigoErro))) {
        this.mudar({ status: 'encerrada' });
        return;
      }
      // Depois de um deploy (4000) a mesa volta em segundos; nas outras quedas, espera um pouco mais a cada vez.
      const espera = ev.code === 4000 ? 300 : Math.min(5000, 400 * 2 ** this.tentativas);
      this.tentativas++;
      this.mudar({ status: 'reconectando' });
      this.timer = setTimeout(() => this.conectar(), espera);
    };
    ws.onerror = () => {};
  }

  private receber(m: MensagemServidor): void {
    const agora = Date.now();
    switch (m.t) {
      case 'bemvindo':
        this.tentativas = 0;
        this.mudar({ status: 'aberta', erro: null, usuario: m.usuario, anonimo: m.anonimo, desvio: m.agora - agora });
        if (this.codigo) this.enviar({ t: 'entrar', codigo: this.codigo, perfil: this.perfil });
        return;
      case 'criada':
        this.pendenteCriar?.ok(m.codigo);
        this.pendenteCriar = null;
        return;
      case 'estado':
        this.mudar({
          sala: m.sala, eu: m.eu, partida: m.partida, seq: m.seq, visao: m.visao, votacao: m.votacao,
          prazos: m.prazos, desvio: m.agora - agora, erro: null, zoeira: m.zoeira !== false,
        });
        return;
      case 'zoeira':
        this.mudar({ zoeira: m.ligada });
        return;
      case 'atualizacao': {
        const primeiro = m.eventos[0]?.seq;
        if (m.partida !== this.estado.partida || (primeiro !== undefined && primeiro !== this.estado.seq + 1)) {
          // Pulou evento ou mudou de partida sem estado novo: pede tudo de novo.
          this.enviar({ t: 'sincronizar' });
          return;
        }
        // Os ouvintes dos eventos rodam antes do redesenho: a animação de cada jogada já está
        // armada quando a carta aparece na mesa.
        this.estado = { ...this.estado, seq: m.seq, visao: m.visao, votacao: m.votacao, prazos: m.prazos, desvio: m.agora - agora };
        if (m.eventos.length) for (const f of this.ouvintesEventos) f(m.eventos, this.estado);
        this.mudar({});
        // Confirma a atualização: a mesa mede a sincronização (p95 no painel).
        this.enviar({ t: 'recebi', seq: m.seq });
        return;
      }
      case 'sala': {
        const i = m.sala.lugares.findIndex((l) => l?.tipo === 'pessoa' && l.id === this.estado.usuario);
        // A mensagem da sala só traz os prazos dela (início, reconexão e a contagem da revanche); os da partida continuam.
        const daSala = (p: Prazo) => p.tipo === 'inicio' || p.tipo === 'reconexao' || p.tipo === 'contagem';
        const prazos = [...this.estado.prazos.filter((p) => !daSala(p)), ...m.prazos];
        this.mudar({ sala: m.sala, eu: i < 0 ? null : (i as Seat), prazos, desvio: m.agora - agora });
        return;
      }
      case 'reacao': case 'opiniao': case 'frase': case 'sentou': case 'reacao_plateia':
        for (const f of this.ouvintesRecado) f(m);
        return;
      case 'erro':
        if (m.codigo === 'falta_celular') { this.mudar({ faltaCelular: true }); return; }
        if (m.codigo === 'sem_partidas') {
          if (this.pendenteCriar) { this.pendenteCriar.falha(Object.assign(new Error(m.mensagem), { codigo: m.codigo })); this.pendenteCriar = null; }
          this.mudar({ semPartidas: true });
          return;
        }
        if (this.pendenteCriar) { this.pendenteCriar.falha(new Error(m.mensagem)); this.pendenteCriar = null; }
        if (FATAIS.includes(m.codigo) || (!this.estado.sala && NA_ENTRADA.includes(m.codigo))) this.mudar({ erro: { codigo: m.codigo, mensagem: m.mensagem } });
        else this.mudar({ aviso: { codigo: m.codigo, mensagem: m.mensagem, em: agora } });
        return;
      case 'ganhos':
        this.mudar({ ganhos: { partida: m.partida, ganhos: m.ganhos } });
        return;
      case 'limite':
        this.mudar({ limite: { partida: this.estado.partida, restantes: m.restantes, origem: m.origem, em: agora } });
        limiteDaMesa(m.restantes, m.origem);
        return;
      case 'rodagem':
        this.mudar({ rodagemAoVivo: { id: (this.estado.rodagemAoVivo?.id ?? 0) + 1, partida: m.partida, lance: m.lance, quanto: m.quanto, total: m.total } });
        return;
      case 'pong':
        return;
    }
  }

  private mudar(parte: Partial<EstadoMesa>): void {
    this.estado = { ...this.estado, ...parte };
    for (const f of this.ouvintes) f();
  }
}
